import { z } from 'zod';
import { getHighestRole, ROLE_DISPLAY, ROLE_HIERARCHY, ROLES } from '../types/rbac-resources';
// Members and who can do what. Every change here decides someone's access to the organization, so each one asks the
// user first, in either mode.
const ASSIGNABLE_ROLES = ROLE_HIERARCHY.filter((role) => role !== ROLES.STRADA_ADMIN);
const roleLabels = ASSIGNABLE_ROLES.map((role) => ROLE_DISPLAY[role].label);
const roleSchema = z
    .enum(roleLabels)
    .describe(ASSIGNABLE_ROLES.map((role) => `${ROLE_DISPLAY[role].label}: ${ROLE_DISPLAY[role].description}`).join('. '));
/** Roles whose members can only edit the agents they're given access to. */
export const AGENT_ACCESS_ROLES = [ROLES.AGENT_MANAGER, ROLES.AGENT_DEVELOPER];
const agentAccessSchema = z.array(z.object({ agentId: z.string(), name: z.string() }));
const memberViewSchema = z.object({
    memberId: z.string(),
    name: z.string(),
    email: z.string(),
    role: z.string(),
    joined: z.string().optional(),
    isYou: z.boolean()
});
export const listMembersToolSchema = {
    name: 'list_members',
    description: "List the organization's members with their email and role.",
    inputSchema: z.object({}),
    outputSchema: z.object({ members: z.array(memberViewSchema) })
};
export const inviteMemberToolSchema = {
    name: 'invite_member',
    description: 'Add someone to the organization with a role, as the Members page does. They can log in with that email right away.',
    inputSchema: z.object({
        name: z.string().min(1),
        email: z.string().email(),
        role: roleSchema,
        agentIds: z
            .array(z.string())
            .optional()
            .describe('For an Agent Manager or Agent Developer: the agents they can edit. They can edit none until given some.')
    }),
    outputSchema: z.object({ email: z.string(), role: z.string() })
};
export const changeMemberRoleToolSchema = {
    name: 'change_member_role',
    description: "Change a member's role. They're signed out everywhere and sign back in with the new role's permissions.",
    inputSchema: z.object({ memberId: z.string(), role: roleSchema }),
    outputSchema: z.object({
        memberId: z.string(),
        role: z.string(),
        signedOut: z
            .boolean()
            .describe('They were signed out, and get the new permissions when they sign back in')
    })
};
export const removeMemberToolSchema = {
    name: 'remove_member',
    description: 'Remove a member from the organization. They lose access immediately.',
    inputSchema: z.object({ memberId: z.string() }),
    outputSchema: z.object({ memberId: z.string(), name: z.string() })
};
export const listMemberAgentAccessToolSchema = {
    name: 'list_member_agent_access',
    description: 'List the agents an Agent Manager or Agent Developer can edit.',
    inputSchema: z.object({ memberId: z.string() }),
    outputSchema: z.object({ memberId: z.string(), agents: agentAccessSchema })
};
export const setMemberAgentAccessToolSchema = {
    name: 'set_member_agent_access',
    description: 'Set which agents an Agent Manager or Agent Developer can edit. Replaces their whole list, so include the agents they keep.',
    inputSchema: z.object({ memberId: z.string(), agentIds: z.array(z.string()) }),
    outputSchema: z.object({
        memberId: z.string(),
        agents: agentAccessSchema.describe('What they can edit now')
    })
};
export const roleIdForLabel = (label) => {
    const role = ASSIGNABLE_ROLES.find((candidate) => ROLE_DISPLAY[candidate].label === label);
    if (!role)
        throw new Error(`There is no role "${label}".`);
    return role;
};
export const memberRole = (member) => getHighestRole(member.roles);
const roleLabel = (role) => ROLE_DISPLAY[role]?.label ?? role;
export const describeMember = (member, currentMemberId) => ({
    memberId: member.id,
    name: member.name,
    email: member.email,
    role: roleLabel(memberRole(member)),
    ...(member.createdAt && { joined: member.createdAt.slice(0, 10) }),
    isYou: member.id === currentMemberId
});
/** Refuses the changes the Members page doesn't offer, with what to do instead. */
export const checkMemberChange = (member, { currentMemberId, change }) => {
    if (memberRole(member) === ROLES.STRADA_ADMIN) {
        throw new Error(`${member.name} is Strada staff. Their access is managed by Strada, not on the Members page.`);
    }
    if (member.id === currentMemberId && change !== 'agent-access') {
        throw new Error(change === 'remove'
            ? "You can't remove yourself from the organization."
            : "Change your own role on the Members page, or ask another Admin to, so you don't lock yourself out by accident.");
    }
    if (change === 'agent-access' && !AGENT_ACCESS_ROLES.includes(memberRole(member))) {
        throw new Error(`${member.name} is ${roleLabel(memberRole(member))}. Only Agent Managers and Agent Developers have per-agent access; other roles' access comes from the role itself.`);
    }
};
//# sourceMappingURL=members.js.map